import { Suspense, lazy, useEffect, useRef, type ReactNode } from "react";
import { usePageContext } from "vike-react/usePageContext";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Analytics from "@/components/analytics/Analytics";
import { trackPageView } from "@/lib/analytics";
import "@/index.css";

const SiteHeader = lazy(() => import("@/components/layout/SiteHeader"));
const SiteFooter = lazy(() => import("@/components/layout/SiteFooter"));

const HeaderSkeleton = () => (
  <div className="sticky top-0 z-50 h-16 w-full border-b border-border/60 bg-background/80 backdrop-blur-md" />
);

const FooterSkeleton = () => (
  <div className="h-40 border-t border-border bg-background" />
);

/**
 * Fires GA4 `page_view` on every SPA navigation. The initial page_view is
 * already auto-sent by gtag at script load (`send_page_view: true` inside
 * Analytics.tsx), so we skip the first render here to avoid double-counting.
 */
function RouteChangeTracker() {
  const pageContext = usePageContext();
  const path = pageContext.urlPathname;
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    trackPageView(path);
  }, [path]);

  return null;
}

export default function Layout({ children }: { children: ReactNode }) {
  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-primary-foreground focus:shadow-glow-primary"
        >
          Lewati ke konten utama
        </a>

        <Suspense fallback={<HeaderSkeleton />}>
          <SiteHeader />
        </Suspense>

        <main id="main">{children}</main>

        <Suspense fallback={<FooterSkeleton />}>
          <SiteFooter />
        </Suspense>
      </div>
      <Toaster />
      <Sonner />
      <Analytics />
      <RouteChangeTracker />
    </TooltipProvider>
  );
}
